import React, { useState, useEffect } from "react";
import { History, Clock, CheckCircle, FileText, Star, Loader2, X } from "lucide-react";
import { Drawer, List, Badge, Button, Typography, Space, Tag, Avatar, notification } from "antd";
import axios from "../../config/axios";
import { format } from "date-fns";

const { Text, Title } = Typography;



interface RecordingVersion {
  _id: string;
  recording_id: string;
  date: string;
  is_default: boolean;
  template_id: number;
  template_name: string;
  has_notes: boolean;
  created_at: string;
  versionNumber?: number;
  isCurrentVersion?: boolean;
}

interface RecordingVersionsResponse {
  recording_id: string;
  total_versions: number;
  versions: RecordingVersion[];
}

interface RecordingVersionsProps {
  recordingId: string;
  selectedTransactionId?: string;
  onVersionSelect: (recordingId: string, versionId: string, isDefault: boolean) => void;
  onVersionUpdate?: () => void; // Callback when a version is set as default
  className?: string;
}

export const RecordingVersions: React.FC<RecordingVersionsProps> = ({
  recordingId,
  selectedTransactionId,
  onVersionSelect,
  onVersionUpdate,
  className = ""
}) => {
  const [versions, setVersions] = useState<RecordingVersion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [totalVersions, setTotalVersions] = useState(0);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);

  useEffect(() => {
    if (recordingId) {
      fetchVersions();
    }
  }, [recordingId]);

  const fetchVersions = async () => {
    if (!recordingId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.get<RecordingVersionsResponse>(
        `/api/scribing/speech/recordings/${recordingId}/versions`
      );
      
      const data = response.data;
      console.log('RecordingVersions - API Response:', data);
      console.log('RecordingVersions - Versions array:', data.versions);
      
      setVersions(data.versions || []);
      setTotalVersions(data.total_versions || 0);
      
    } catch (err: any) {
      console.error("Error fetching recording versions:", err);
      setError("Failed to load recording versions");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVersionSelect = (version: RecordingVersion) => {
    console.log('RecordingVersions - handleVersionSelect called with version:', version);
    console.log('RecordingVersions - Passing recording_id:', version.recording_id);
    console.log('RecordingVersions - Passing version _id:', version._id);
    console.log('RecordingVersions - Parent recordingId prop:', recordingId);
    
    onVersionSelect(version.recording_id, version._id, version.is_default);
    setIsDrawerOpen(false); // Close drawer after selection
  };

  const handleSetAsDefault = async (version: RecordingVersion, event: React.MouseEvent) => {
    // Prevent the version selection from triggering
    event.stopPropagation();
    
    if (version.is_default || settingDefaultId) return;
    
    setSettingDefaultId(version._id);
    
    try {
      const response = await axios.put(
        `/api/scribing/speech/recordings/${version.recording_id}/set-default`,
        {
          recording_id: version.recording_id
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Check for success response
      if (response.data && response.data.success) {
        // Show success notification in UI
        notification.success({
          message: "Version Set as Default",
          description: response.data.message || "Recording has been successfully set as the default version",
          placement: "topRight",
          duration: 3
        });
        
        console.log("Recording set as default successfully:", response.data.message);
        
        // Refresh the versions list to show the updated default
        await fetchVersions();
        
        // Automatically select the new default version to show its content
        onVersionSelect(version.recording_id, version._id, true);
        
        // Close the drawer since user probably wants to see the new default content
        setIsDrawerOpen(false);
        
        // Notify parent component about the update to refresh main content
        if (onVersionUpdate) {
          onVersionUpdate();
        }
      } else {
        throw new Error("Failed to set recording as default");
      }
      
    } catch (err: any) {
      console.error("Error setting version as default:", err);
      setError("Failed to set version as default");
    } finally {
      setSettingDefaultId(null);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy • h:mm a');
    } catch {
      return dateString;
    }
  };

  const getDefaultVersion = () => {
    return versions.find(v => v.is_default);
  };

  const getNonDefaultVersions = () => {
    return versions.filter(v => !v.is_default).sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  };

  const getAllVersionsSorted = () => {
    // Sort all versions by creation date (newest first) and assign version numbers
    const sorted = [...versions].sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    
    return sorted.map((version, index) => ({
      ...version,
      versionNumber: sorted.length - index, // Newest gets highest number
      isCurrentVersion: version.is_default
    }));
  };

  if (isLoading) {
    return (
      <div className={`bg-white border border-gray-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <History className="h-4 w-4 animate-pulse" />
          Loading versions...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center gap-2 text-sm text-red-600">
          <History className="h-4 w-4" />
          {error}
        </div>
      </div>
    );
  }

  if (totalVersions <= 1) {
    return null; // Don't show component if there's only one version
  }

  const defaultVersion = getDefaultVersion();
  const otherVersions = getNonDefaultVersions();

  return (
    <>
      <div className={`bg-white border border-gray-200 rounded-lg ${className}`}>
        {/* Header - Click to open drawer */}
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setIsDrawerOpen(true)}
        >
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">
              {totalVersions} Version{totalVersions !== 1 ? 's' : ''} Available
            </span>
          </div>
        </div>
      </div>

      {/* Apple-inspired Versions Drawer */}
      <Drawer
        title={null}
        placement="right"
        onClose={() => setIsDrawerOpen(false)}
        open={isDrawerOpen}
        width={420}
        className="apple-versions-drawer"
        styles={{
          body: {
            padding: '0',
            background: '#fbfbfd',
          },
          header: {
            border: 'none',
            padding: '0',
          }
        }}
        closeIcon={
          <div className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-all duration-200">
            <X className="h-4 w-4 text-gray-600" />
          </div>
        }
      >
        {/* Apple-style Header */}
        <div className="px-6 py-6 bg-white border-b border-gray-100">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 tracking-tight">Versions</h2>
            <p className="text-sm text-gray-500 mt-1 font-medium">
              {totalVersions} version{totalVersions !== 1 ? 's' : ''} available
            </p>
          </div>
        </div>

                {/* Apple-inspired Clean Cards */}
        <div className="px-6 pb-6 space-y-2">
          {getAllVersionsSorted().map((version) => (
            <div
              key={version._id}
              className={`group relative rounded-2xl transition-all duration-300 cursor-pointer ${
                selectedTransactionId === version._id 
                  ? 'bg-blue-50/80 scale-[1.01]' 
                  : 'bg-white/60 hover:bg-white/90 hover:scale-[1.005]'
              }`}
              style={{
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
              }}
              onClick={() => handleVersionSelect(version)}
            >
              <div className="p-5">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${
                      version.isCurrentVersion 
                        ? 'bg-green-500 shadow-lg shadow-green-500/25' 
                        : 'bg-gray-100'
                    }`}>
                      {version.isCurrentVersion ? (
                        <CheckCircle className="h-6 w-6 text-white" />
                      ) : (
                        <History className="h-6 w-6 text-gray-500" />
                      )}
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg tracking-tight">
                        {version.isCurrentVersion 
                          ? 'Current Version' 
                          : `Version ${version.versionNumber}`
                        }
                      </h3>
                      <p className="text-sm text-gray-500 font-medium mt-0.5">
                        <Clock className="h-3.5 w-3.5 inline mr-1.5" />
                        {formatDate(version.created_at)}
                      </p>
                      <p className="text-sm text-gray-500 font-medium mt-0.5">
                        <Clock className="h-3.5 w-3.5 inline mr-1.5" />
                        {formatDate(version.recording_id)}
                      </p>
                    </div>
                  </div>

                  {/* Action Button */}
                  {!version.isCurrentVersion && (
                    <button
                      onClick={(e) => handleSetAsDefault(version, e)}
                      disabled={settingDefaultId === version._id}
                      className="opacity-0 group-hover:opacity-100 transition-all duration-300 p-3 rounded-xl bg-white/80 hover:bg-blue-50 disabled:opacity-50 shadow-sm"
                      title="Set as current version"
                    >
                      {settingDefaultId === version._id ? (
                        <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                      ) : (
                        <Star className="h-5 w-5 text-gray-400 hover:text-blue-600" />
                      )}
                    </button>
                  )}
                </div>

                {/* Template Badge */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-3 py-1.5 rounded-xl text-sm font-medium bg-blue-100 text-blue-700">
                      <FileText className="h-4 w-4 mr-2" />
                      {version.template_name}
                    </span>
                  </div>

                  {/* Selection Indicator */}
                  {selectedTransactionId === version._id && (
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center shadow-lg shadow-blue-500/25">
                      <CheckCircle className="h-4 w-4 text-white" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Drawer>
    </>
  );
};

export default RecordingVersions; 